import { UpdateProfileStatus, useCustomerDetails, useUpdateFinancialInfo } from '@egw/api'
import { Button, Field, Input, ModalDrawer, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { addCentsToCurrency, formatInputToCurrency } from '@egw/utils'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { useEffect, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FinancialField } from '../../types'

const log = new Logger('Annual Income')

interface AnnualIncomeFormInterface {
  [FinancialField.PERSONAL_ANNUAL_GROSS_INCOME]: string
}

export const EditAnnualIncomeField: FC = () => {
  const { t } = useTranslation('features-settings')
  const { isOpen, openModal, closeModal } = useModalState()

  const [updateError, setUpdateError] = useState<boolean>(false)

  const { data, refetch, isRefetching } = useCustomerDetails()

  const value = data?.customer.personalAnnualGrossIncome

  const { mutate: updateFinancial, isLoading } = useUpdateFinancialInfo({
    onSuccess: (res) => {
      if (res.__typename === 'UpdateProfileError' || res.status === UpdateProfileStatus.FAILED) {
        log.error('Error updating Annual Income', { data })
        setUpdateError(true)
      } else if (res.status === UpdateProfileStatus.COMPLETE) {
        void refetch()
          .catch((error: unknown) => {
            log.error(error)
          })
          .finally(() => {
            setUpdateError(false)
            closeModal()
            reset()
          })
      }
    },
    onError: () => {
      setUpdateError(true)
    },
  })

  const defaultValues = {
    [FinancialField.PERSONAL_ANNUAL_GROSS_INCOME]: value?.toString(),
  }

  const { handleSubmit, control, reset, formState, getValues, setValue } = useForm<AnnualIncomeFormInterface>({
    mode: 'all',
    defaultValues,
  })

  useEffect(() => {
    if (data) {
      reset({
        [FinancialField.PERSONAL_ANNUAL_GROSS_INCOME]: value?.toString(),
      })
    }
  }, [data, reset, value])

  const onSubmit = async (formData: any) => {
    setUpdateError(false)
    log.debug('Submitting Form', { formData })
    updateFinancial({ personalAnnualGrossIncome: parseFloat(formData.personalAnnualGrossIncome.replace(/[$,]/g, '')) })
  }

  function addCents(): void {
    const currentValue = getValues(FinancialField.PERSONAL_ANNUAL_GROSS_INCOME)
    Boolean(currentValue) && setValue(FinancialField.PERSONAL_ANNUAL_GROSS_INCOME, addCentsToCurrency(currentValue))
  }

  return value !== undefined ? (
    <>
      <Field
        valueClassName="font-light"
        className="w-full justify-between border-list mb-0 py-4 px-2"
        onEdit={openModal}
        value={t('financial_labels.annual_income')}
        rightValue={formatInputToCurrency(value.toString(), true)}
        isPII
      />
      <ModalDrawer title={t('financial_labels.annual_income')} isOpen={isOpen} onClose={closeModal}>
        <form
          id="updateAnnualIncomeForm"
          className="flex flex-col gap-4  h-full md:h-screen justify-between"
          onSubmit={(e) => {
            e.preventDefault()
            void handleSubmit(onSubmit)()
          }}
        >
          <div className="flex flex-col gap-10">
            <div>
              <p className="mt-4 mb-4 font-semibold">{t('update_annual_income.your_update_annual_income')}</p>
              <Controller
                name={FinancialField.PERSONAL_ANNUAL_GROSS_INCOME}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('update_annual_income.household_income')}
                    onBlur={addCents}
                    value={formatInputToCurrency(field.value, true)}
                    valueFormatter={(formattedValue) => formatInputToCurrency(formattedValue, true)}
                    inputMode="decimal"
                    isPII
                  />
                )}
              />
            </div>
            {updateError ? (
              <div className="flex bg-alertErrorBg p-3 rounded-md">
                <InformationCircleIcon className="w-6 h-6 fill-red-500" />
                <p className="text-sm text-alertErrorText ml-2">{t('common.update_error')} </p>
              </div>
            ) : null}
          </div>
          <Button
            id="updateAnnualIncome"
            type="submit"
            isLoading={isLoading || isRefetching}
            isDisabled={!formState.isValid}
          >
            {t('common.confirm')}
          </Button>
        </form>
      </ModalDrawer>
    </>
  ) : null
}
